import { put, call, takeLatest, all } from 'redux-saga/effects';
import * as actions from './actions';
import * as api from './api';
import * as types from './types';

import * as routes from 'app/routes';
import { createSubmissionError } from 'forms/utils';

import { info, error } from 'notifications/actions';

export function* authenticateUser() {
  try {
    const resp = yield call(api.authenticate);
    yield put(actions.authenticationSucceeded(resp));
  }
  catch (err) {
    yield put(actions.authenticationFailed(err));
    yield put(routes.login());
  }
}

export function* requestResetCode({ payload }) {
  try {
    const resp = yield call(api.requestResetCode, payload);
    yield put(actions.requestResetCode.success(resp));
    yield put(actions.resetCodeSent(payload.email));
    yield put(info('requestResetCode', 'A reset code has been sent to your email address. It will be valid for 15 minutes.'));
  }
  catch (err) {
    const { response } = err;
    const submissionError = createSubmissionError(response);
    yield put(actions.requestResetCode.failure(submissionError));
    yield put(error('requestResetCode', response.data.detail));
  }
}

export function* resetPassword({ payload }) {
  try {
    const resp = yield call(api.resetPassword, payload);
    yield put(actions.resetPassword.success(resp));
    yield put(info('general', 'Your password has been reset'));
    yield put(routes.login());
  }
  catch (err) {
    const { response } = err;
    const submissionError = createSubmissionError(response);
    yield put(actions.resetPassword.failure(submissionError));
    if (response.status === 403) {
      yield put(error('resetPassword', 'Either the code was wrong, or it has expired'));
    }
  }
}

export function* logout(action) {
  try {
    yield put(actions.logoutPending());
    yield call(api.logout);
    yield put(actions.resetStore());
    yield put(actions.logoutSucceeded());
    yield put(info('general', 'You have been signed out'));
    yield put(routes.login());
  }
  catch (err) {
    yield put(actions.logoutFailed());
    yield put(error('general', 'Could not sign out at this time'));
  } 
}

export function* createAddress({ payload }) {
  try {
    yield put(actions.createAddressPending());
    const resp = yield call(api.createAddress, payload);
    yield put(actions.createAddressSucceeded(resp));
    return resp;
  }
  catch (err) {
    yield put(actions.createAddressFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to create address',
      success: false,
    };
  }
}

export function* updateAddress({ payload: { id, ...details } }) {
  try {
    yield put(actions.updateAddressPending());
    const resp = yield call(api.updateAddress, id, details);
    yield put(actions.updateAddressSucceeded(resp));
    return resp;
  }
  catch (err) {
    yield put(actions.updateAddressFailed(err));
    return {
      error: (err.response.data && err.response.data.detail) || 'Failed to update address',
      success: false,
    };
  }
}

export function* watchAuthenticateUser() {
  yield takeLatest(types.AUTHENTICATE, authenticateUser);
}

export function* watchRequestResetCode() {
  yield takeLatest(actions.requestResetCode.REQUEST, requestResetCode);
}

export function* watchResetPassword() {
  yield takeLatest(actions.resetPassword.REQUEST, resetPassword);
}

export function* watchLogout() {
  yield takeLatest(types.LOGOUT, logout);
}

export function* watchAuth() {
  yield all([
    call(watchAuthenticateUser),
    call(watchRequestResetCode),
    call(watchResetPassword),
    call(watchLogout),
  ]);
}
